import { useMemo } from 'react';
import { useModalContext } from '../components/modal-context/ModalContext';
import { usePresentationContext } from './usePresentationContext';

type BadgePosition = 'modal' | 'header' | 'presentation' | 'preview';

/**
 * Custom hook to determine the badge position based on modal and presentation states
 * @returns The current badge position ('modal', 'header', or 'presentation')
 */
export const useBadgePosition = (): BadgePosition => {
  const { isModalOpen } = useModalContext();
  
  // Safely access the presentation context
  let isPresenting = false;
  let isPreview = false;
  try {
    const presentationContext = usePresentationContext();
    isPresenting = presentationContext?.isPresenting || false;
    isPreview = presentationContext.isPreview || false;
  } catch (error) {
    // Silently handle errors to prevent excessive logging
    isPresenting = false;
    isPreview = false;
  }
  
  // Memoize the badge position calculation to prevent unnecessary re-renders
  const badgePosition = useMemo(() => {
    // Simple logic: presentation takes precedence, then modal, then header
    if (isPresenting) {
      return 'presentation';
    } else if (isPreview) {
      return 'preview';
    } else if (isModalOpen) {
      return 'modal';
    } else {
      return 'header';
    }
  }, [isPresenting, isPreview, isModalOpen]);
  
  return badgePosition;
};
